import { BusinessSellerPanel, type BusinessSeller } from "@/components/BusinessSellerPanel";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { MapPin, CalendarDays, Check, Pencil, AlertTriangle } from "lucide-react";
import { categoryIcon, categoryLabel } from "@/lib/categories";
import { AdGallery } from "@/components/AdGallery";
import { parseFocus } from "@/lib/image-focus";
import { formatPublished, relativePublished } from "@/lib/format-date";
import { AdMap } from "@/components/AdMap";
import { ShareButtons } from "@/components/ShareButtons";
import { FavoriteButton } from "@/components/FavoriteButton";

import { AdHistory } from "@/components/AdHistory";
import { adImageSrc } from "@/lib/image-src";
import { ContactSeller } from "@/components/ContactSeller";
import { RelatedAds } from "@/components/RelatedAds";
import { AdminAdInfoPanel } from "@/components/AdminAdInfo";
import { ReportAd } from "@/components/ReportAd";
import { AdViewTracker } from "@/components/AdViewTracker";
import { AuctionPanel } from "@/components/AuctionPanel";
import { AuctionDevBadge } from "@/components/AuctionDevBadge";
import { useAuctionsEnabled } from "@/lib/auction-availability";
import { SafeDealPanel } from "@/components/SafeDealPanel";
import { BackLink } from "@/components/BackLink";
import { useLocale } from "@/lib/i18n/locale-context";
import { formatPrice } from "@/lib/i18n/format";


import { getAdBySlug } from "@/lib/ads.functions";
import { SITE_URL, abs } from "@/lib/site-url";
import type { Tables } from "@/integrations/supabase/types";
import { OfferBox } from "@/components/offers/OfferBox";

export const Route = createFileRoute("/a/$slug")({
  validateSearch: (search: Record<string, unknown>): { ny?: true } => {
    const raw = search.ny;
    return raw === true || raw === "true" || raw === "1" || raw === 1 ? { ny: true } : {};
  },
  head: ({ params, loaderData }) => {
    const data = loaderData as unknown as
      | { ad: Tables<"ads"> | undefined; origin: string }
      | undefined;
    const ad = data?.ad;
    const origin = data?.origin ?? "";
    const priceText = ad ? `${ad.price.toLocaleString("sv-SE")} kr` : "";
    const place = ad?.location ? ` i ${ad.location}` : "";
    const title = ad
      ? `${ad.title} till salu${place} – ${priceText} | Sellavo`
      : "Annons — Sellavo";
    const description = ad
      ? `${ad.title} säljes${place} för ${priceText}. ${ad.description.slice(0, 110)}…`
      : "Se annonsen på Sellavo – köp och sälj begagnat snabbt och enkelt.";

    const imagePath = ad ? adImageSrc(ad.image_url) : undefined;
    const imageBase = origin && /^https?:\/\//i.test(origin) ? origin : SITE_URL;
    const imageUrl = imagePath
      ? imagePath.startsWith("http")
        ? imagePath
        : `${imageBase}${imagePath}`
      : undefined;
    const canonical = abs(`/a/${params.slug}`);

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { property: "og:url", content: canonical },
        { property: "og:site_name", content: "Sellavo" },
        ...(imageUrl
          ? [
              { property: "og:image", content: imageUrl },
              { property: "og:image:secure_url", content: imageUrl },
              { property: "og:image:width", content: "1200" },
              { property: "og:image:height", content: "900" },
              { property: "og:image:alt", content: ad ? ad.title : "Annons på Sellavo" },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        ...(imageUrl
          ? [
              { name: "twitter:image", content: imageUrl },
              { name: "twitter:image:alt", content: ad ? ad.title : "Annons på Sellavo" },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: ad
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Product",
                name: ad.title,
                description: ad.description,
                image: imageUrl,
                offers: {
                  "@type": "Offer",
                  price: ad.price,
                  priceCurrency: "SEK",
                  availability: "https://schema.org/InStock",
                },
              }),
            },
          ]
        : undefined,
    };
  },
  loader: async ({ params }) => {
    const [ad, origin] = await Promise.all([
      getAdBySlug({ data: { slug: params.slug } }).catch(() => null),
      import("@/lib/origin.functions")
        .then((m) => m.getRequestOrigin({}))
        .catch(() => ""),
    ]);
    if (!ad) throw notFound();
    return { ad, origin };
  },
  errorComponent: AdNotFound,
  component: AdPage,
  pendingComponent: AdPending,
  notFoundComponent: AdNotFound,
});

function AdPending() {
  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <div className="animate-pulse space-y-4">
        <div className="h-4 w-24 rounded bg-white/10" />
        <div className="aspect-video w-full rounded-lg bg-white/10" />
        <div className="h-7 w-2/3 rounded bg-white/10" />
        <div className="h-5 w-32 rounded bg-white/10" />
        <div className="h-24 w-full rounded bg-white/10" />
      </div>
    </main>
  );
}

function AdPage() {
  const auctionsOn = useAuctionsEnabled();
  const { lang } = useLocale();
  const tx =
    lang === "en"
      ? {
          published: "Your listing is published!",
          publishedHint:
            "This is how buyers see it. Feel free to share it – or adjust text and price whenever you like.",
          editAd: "Edit listing",
          myAds: "My listings",
          hidden: "Hidden listing – only visible to you",
          checkPrice: "Check the price. ",
          low: "The price is unusually low for this kind of item. Be extra careful before you pay.",
          high: "The price is clearly above what similar items usually cost. Compare before you buy.",
          publishedOn: "Published",
          createOwn: "Create your own listing",
          notFound: "Listing not found",
          notFoundHint: "The link may be wrong or the listing has been removed.",
        }
      : lang === "es"
        ? {
            published: "¡Tu anuncio está publicado!",
            publishedHint:
              "Así lo ven los compradores. Compártelo si quieres, o ajusta el texto y el precio cuando desees.",
            editAd: "Editar anuncio",
            myAds: "Mis anuncios",
            hidden: "Anuncio oculto: solo lo ves tú",
            checkPrice: "Comprueba el precio. ",
            low: "El precio es inusualmente bajo para este tipo de objeto. Ten cuidado antes de pagar.",
            high: "El precio está claramente por encima de lo habitual para objetos similares. Compara antes de comprar.",
            publishedOn: "Publicado",
            createOwn: "Crea tu propio anuncio",
            notFound: "Anuncio no encontrado",
            notFoundHint: "El enlace puede ser incorrecto o el anuncio se ha eliminado.",
          }
        : {
            published: "Din annons är publicerad!",
            publishedHint:
              "Så här ser den ut för köpare. Dela den gärna vidare – eller justera text och pris när du vill.",
            editAd: "Redigera annonsen",
            myAds: "Mina annonser",
            hidden: "Dold annons – syns bara för dig",
            checkPrice: "Kontrollera priset. ",
            low: "Priset är ovanligt lågt för den här typen av pryl. Var extra noga innan du betalar.",
            high: "Priset ligger klart över vad liknande prylar brukar kosta. Jämför gärna innan du köper.",
            publishedOn: "Publicerad",
            createOwn: "Skapa din egen annons",
            notFound: "Annonsen hittades inte",
            notFoundHint: "Länken kan vara fel eller annonsen har tagits bort.",
          };
  const { ad } = Route.useLoaderData() as unknown as {
    ad: Tables<"ads"> & { seller_business?: BusinessSeller | null };
  };
  const sellerBusiness = ad.seller_business ?? null;
  const { ny } = Route.useSearch();


  const imageUrl = adImageSrc(ad.image_url);
  const images = ad.image_urls?.length ? ad.image_urls : [ad.image_url];
  const focus = parseFocus((ad as { image_focus?: unknown }).image_focus);

  return (
    <main className="bg-background px-4 py-10 sm:px-6 lg:px-8">
      <AdViewTracker slug={ad.slug} />
      <div className="mx-auto max-w-2xl">
        <BackLink />

        {ny && (
          <div className="mt-6 rounded-md border border-primary/30 bg-ocean-100 p-5">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15">
                <Check className="h-5 w-5 text-primary" />
              </span>
              <div>
                <p className="font-heading text-base font-bold text-ocean-700">{tx.published}</p>
                <p className="mt-1 text-sm text-ocean-700/80">{tx.publishedHint}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to="/redigera/$slug"
                    params={{ slug: ad.slug }}
                    className="inline-flex items-center gap-2 rounded-md bg-ocean-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-ocean-800"
                  >
                    <Pencil className="h-4 w-4" /> {tx.editAd}
                  </Link>

                  <Link
                    to="/mina-annonser"
                    className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-ocean-700 underline underline-offset-2"
                  >
                    {tx.myAds}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <article className="mt-6 overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <AdGallery
            paths={images}
            focus={focus}
            title={ad.title}
            videoUrl={(ad as { video_url?: string | null }).video_url ?? null}
            overlay={<FavoriteButton slug={ad.slug} size="lg" shape="round" />}
          />
          <div className="p-6 sm:p-8">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <Link
                to="/annonser"
                search={{ kategori: ad.category ?? "ovrigt" }}
                className="inline-flex items-center gap-1.5 rounded-md bg-ocean-100 px-3 py-1 font-medium text-ocean-700 transition-colors hover:bg-ocean-200"
              >
                {(() => {
                  const Icon = categoryIcon(ad.category ?? "ovrigt");
                  return <Icon className="h-4 w-4" />;
                })()}
                {categoryLabel(ad.category ?? "ovrigt")}
              </Link>

              {ad.location && (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <MapPin className="h-4 w-4" /> {ad.location}
                </span>
              )}
            </div>
            {(ad as { hidden?: boolean }).hidden && (
              <p className="mt-3 inline-flex items-center gap-2 rounded-md border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300">
                {tx.hidden}
              </p>
            )}

            <h1 className="mt-3 break-words font-heading text-2xl font-bold text-foreground sm:text-3xl">
              {ad.title}
            </h1>
            <p className="mt-4 whitespace-pre-line break-words text-lg leading-relaxed text-foreground">
              {ad.description}
            </p>
            {ad.sale_kind === "auction" && auctionsOn ? (
              <>
                <div className="mt-6">
                  <AuctionDevBadge />
                </div>
                <AuctionPanel slug={ad.slug} sellerId={ad.user_id} />
              </>
            ) : (
              <p className="mt-6 font-heading text-3xl font-bold text-primary">
                {formatPrice(ad.price, ad.country === "ES" ? "ES" : "SE")}
                {sellerBusiness && sellerBusiness.vatRegistered !== false && (
                  <span className="mt-1 block text-sm font-medium text-muted-foreground">
                    {(ad as { price_includes_vat?: boolean }).price_includes_vat === false
                      ? (lang === "en" ? "excl. VAT" : lang === "es" ? "sin IVA" : "exkl. moms")
                      : `${lang === "en" ? "incl. VAT" : lang === "es" ? "IVA incluido" : "inkl. moms"} · ${formatPrice(Math.round(ad.price / (ad.country === "ES" ? 1.21 : 1.25)), ad.country === "ES" ? "ES" : "SE")} ${lang === "en" ? "excl. VAT" : lang === "es" ? "sin IVA" : "exkl. moms"}`}
                  </span>
                )}
              </p>
            )}
            {(ad.price_flag === "low" || ad.price_flag === "high") && (
              <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                <p className="text-sm text-foreground">
                  <span className="font-semibold">{tx.checkPrice}</span>
                  {ad.price_flag_note || (ad.price_flag === "low" ? tx.low : tx.high)}
                </p>
              </div>
            )}
            {ad.created_at && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                <CalendarDays className="h-4 w-4" /> {tx.publishedOn}{" "}
                {formatPublished(ad.created_at, lang)}
                <span className="text-muted-foreground/70">
                  ({relativePublished(ad.created_at, lang)})
                </span>
              </p>
            )}

            {ad.latitude != null && ad.longitude != null && (
              <AdMap
                latitude={ad.latitude}
                longitude={ad.longitude}
                seed={ad.slug}
                country={(ad as { country?: string | null }).country ?? "SE"}
                label={ad.location || ad.address}
              />
            )}

            {sellerBusiness && <BusinessSellerPanel seller={sellerBusiness} />}

            <SafeDealPanel slug={ad.slug} country={ad.country === "ES" ? "ES" : "SE"} />

            <OfferBox slug={ad.slug} price={ad.price} country={ad.country === "ES" ? "ES" : "SE"} />

            <ContactSeller
              slug={ad.slug}
              title={ad.title}
              sellerName={ad.contact_name}
              sellerId={ad.user_id}
            />


            <div className="mt-6 flex flex-wrap items-center gap-3">
              <ShareButtons title={ad.title} price={ad.price} />
            </div>


            <AdHistory slug={ad.slug} />

            <div className="mt-6">
              <ReportAd slug={ad.slug} title={ad.title} />
            </div>
          </div>
        </article>

        <RelatedAds
          slug={ad.slug}
          category={ad.category}
          latitude={ad.latitude}
          longitude={ad.longitude}
        />

        <AdminAdInfoPanel slug={ad.slug} />

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-lg bg-ocean-100 px-6 py-3 font-heading font-semibold text-ocean-700 transition-colors hover:bg-ocean-100/80"
          >
            {tx.createOwn}
          </Link>
        </div>
      </div>
    </main>
  );
}

function AdNotFound() {
  const { lang } = useLocale();
  const title =
    lang === "en" ? "Listing not found" : lang === "es" ? "Anuncio no encontrado" : "Annonsen hittades inte";
  const hint =
    lang === "en"
      ? "The link may be wrong or the listing has been removed."
      : lang === "es"
        ? "El enlace puede ser incorrecto o el anuncio se ha eliminado."
        : "Länken kan vara fel eller annonsen har tagits bort.";
  return (
    <main className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
      <h1 className="font-heading text-4xl font-bold text-foreground">{title}</h1>
      <p className="mt-2 text-muted-foreground">{hint}</p>
      <BackLink className="mt-6" fallbackTo="/annonser" />
    </main>
  );
}

